import React, { useContext, useId } from 'react';
import { LangContext } from '../../contexts/langContext';
import { VARNA } from '../../static/jyotish_varna';
import { TooltipH } from '../UiKit/TooltipH';
import '../css/jyotish-chart.css';

/** Clockwise from top: brahmin → kshatriya → shudra → vaishya (left). */
const AXES = [
  { id: 'brahmin', angle: 0 },
  { id: 'kshatriya', angle: 90 },
  { id: 'shudra', angle: 180 },
  { id: 'vaishya', angle: 270 },
];

/** Role → segment color on the data shape. */
const ROLE_COLORS = {
  core: '#1c1c1c',
  inner: '#7a1520',
  manifestation: '#e6c43a',
  none: '#d4d0c8',
};

const SIZE = 240;
const CX = SIZE / 2;
const CY = SIZE / 2;
/** Vertex radius; visual tip is shorter because of rounding. */
const R = 108;
const RINGS = 4;
/** Gap from the rounded outer tip to the emoji center. */
const LABEL_GAP = 14;
/** Corner softness for rings and data blob (like the reference). */
const CORNER = 48;
/**
 * Vertical shear: right tip (kshatriya) above center, left tip (vaishya) below.
 * Applied as Δy = −SKEW · (x − CX).
 */
const SKEW = 0.16;
/**
 * Non-linear ring radii (fraction of R): bands near the center are much larger.
 * Level 1..4 → ~55% / 22% / 14% / 9% of the radius each.
 */
const RING_T = [0, 0.6, 0.74, 0.88, 1];

const INDICATOR_LABEL_KEYS = {
  moon_D1: 'planet_Moon',
  moon_D9: 'jyotish_moon_d9',
  ascendant_D1: 'jyotish_lagna_label',
  sun_D1: 'planet_Sun',
};

const pointAt = (angleDeg, radius) => {
  const rad = (angleDeg * Math.PI) / 180;
  return {
    x: CX + radius * Math.sin(rad),
    y: CY - radius * Math.cos(rad),
  };
};

/** Shear: kshatriya above the midline, vaishya below. */
const shapePoint = ({ x, y }) => ({
  x,
  y: y - SKEW * (x - CX),
});

const cornerCut = (ringRadius, corner) => {
  const edge = ringRadius * Math.SQRT2;
  return Math.min(corner, edge * 0.48);
};

/**
 * Radius along an axis to the tip of the rounded diamond (Q does not reach the sharp vertex).
 * Stops the “long sticks” past the outer shape.
 */
const roundedAxisRadius = (ringRadius, corner) => {
  const c = cornerCut(ringRadius, corner);
  const curr = pointAt(0, ringRadius);
  const prev = pointAt(270, ringRadius);
  const next = pointAt(90, ringRadius);
  const len = Math.hypot(curr.x - prev.x, curr.y - prev.y) || 1;
  const start = {
    x: curr.x - ((curr.x - prev.x) / len) * c,
    y: curr.y - ((curr.y - prev.y) / len) * c,
  };
  const end = {
    x: curr.x + ((next.x - curr.x) / len) * c,
    y: curr.y + ((next.y - curr.y) / len) * c,
  };
  // Midpoint of quadratic Bezier (t = 0.5) lies on the axis by symmetry.
  const midY = 0.25 * start.y + 0.5 * curr.y + 0.25 * end.y;
  return CY - midY;
};

/** Soft diamond path: straight edges with quadratic rounded vertices. */
const roundedPath = (points, corner = CORNER) => {
  const n = points.length;
  if (n < 3) return '';

  const parts = [];
  for (let i = 0; i < n; i += 1) {
    const prev = points[(i - 1 + n) % n];
    const curr = points[i];
    const next = points[(i + 1) % n];

    const v1x = curr.x - prev.x;
    const v1y = curr.y - prev.y;
    const v2x = next.x - curr.x;
    const v2y = next.y - curr.y;
    const len1 = Math.hypot(v1x, v1y) || 1;
    const len2 = Math.hypot(v2x, v2y) || 1;
    const r1 = Math.min(corner, len1 * 0.48);
    const r2 = Math.min(corner, len2 * 0.48);

    const start = {
      x: curr.x - (v1x / len1) * r1,
      y: curr.y - (v1y / len1) * r1,
    };
    const end = {
      x: curr.x + (v2x / len2) * r2,
      y: curr.y + (v2y / len2) * r2,
    };

    if (i === 0) parts.push(`M ${start.x} ${start.y}`);
    else parts.push(`L ${start.x} ${start.y}`);
    parts.push(`Q ${curr.x} ${curr.y} ${end.x} ${end.y}`);
  }
  parts.push('Z');
  return parts.join(' ');
};

const axisPoints = (radii) =>
  AXES.map(({ angle }, i) => shapePoint(pointAt(angle, radii[i])));

const radiusForCount = (count, max) => {
  if (count <= 0 || max <= 0) return 0;
  const clamped = Math.min(count, max);
  if (max === RINGS && RING_T[clamped] != null) return R * RING_T[clamped];
  const t = clamped / max;
  return R * (1 - (1 - t) ** 1.7);
};

const rolesForVarna = (varnaId, levels, $ui) => {
  if (!levels || !varnaId) return [];
  const roles = [];
  if (levels.core === varnaId) roles.push($ui('jyotish_varna_primary'));
  if (levels.inner === varnaId) roles.push($ui('jyotish_varna_inner'));
  if ((levels.manifestation || []).some((m) => m.varna === varnaId)) {
    roles.push($ui('jyotish_varna_manifestation'));
  }
  return roles;
};

/** Prefer core → inner → manifestation for an axis segment. */
const roleKeyForVarna = (varnaId, levels, count) => {
  if (!count || !varnaId || !levels) return 'none';
  if (levels.core === varnaId) return 'core';
  if (levels.inner === varnaId) return 'inner';
  if ((levels.manifestation || []).some((m) => m.varna === varnaId)) return 'manifestation';
  return 'none';
};

/**
 * Diamond radar of Varna distribution.
 * Top = brahmin, right = kshatriya, bottom = shudra, left = vaishya (masked themes).
 */
export function VarnaRadarChart({ distribution, profile }) {
  const { $ui, $jyotish, language } = useContext(LangContext);
  const uid = useId().replace(/:/g, '');
  const clipId = `varna-radar-clip-${uid}`;

  if (!distribution) return null;

  const values = AXES.map(({ id }) => distribution[id] || 0);
  const max = Math.max(RINGS, ...values);
  const dataRadii = values.map((v) => radiusForCount(v, max));
  // Keep a tiny radius at zero so the rounded path stays smooth instead of collapsing to the center.
  const dataPoints = axisPoints(dataRadii.map((r) => Math.max(r, 6)));
  const dataCorner = Math.min(CORNER, 22 + Math.min(...dataRadii.filter(Boolean), R) * 0.2);
  const dataPath = roundedPath(dataPoints, dataCorner);
  const axisEndR = roundedAxisRadius(R, CORNER);
  const labelR = axisEndR + LABEL_GAP;
  const segmentColors = AXES.map(({ id }, i) =>
    ROLE_COLORS[roleKeyForVarna(id, profile?.levels, values[i])]
  );
  // Smooth blend around the shape: top → right → bottom → left → top.
  const conicFill = `conic-gradient(from 0deg at 50% 50%, ${[
    `${segmentColors[0]} 0deg`,
    `${segmentColors[1]} 90deg`,
    `${segmentColors[2]} 180deg`,
    `${segmentColors[3]} 270deg`,
    `${segmentColors[0]} 360deg`,
  ].join(', ')})`;

  const theme = (id) => VARNA[id].theme[language] || VARNA[id].theme.en;
  const emoji = (id) => $jyotish(VARNA[id].uiKey, 'emoji');

  const tooltipTitle = (varnaId, count) => {
    if (!count) {
      return (
        <div className="_fv _fgap02">
          <span className="_o06">{theme(varnaId)}</span>
          <span className="_o06">{$ui('jyotish_varna_no_activations')}</span>
        </div>
      );
    }
    const roles = rolesForVarna(varnaId, profile?.levels, $ui);
    const lines = (profile?.indicators || []).filter((one) => one.varna === varnaId);
    return (
      <div className="_fv _fgap02">
        {roles.map((role) => <b key={role}>{role}</b>)}
        <span className="_o06">{theme(varnaId)} · {count}</span>
        {lines.map((one) => (
          <span key={`${one.source}_${one.chart}`}>
            {$ui(INDICATOR_LABEL_KEYS[`${one.source}_${one.chart}`])}
            {one.sign ? <> · {$ui(one.sign)}</> : null}
          </span>
        ))}
      </div>
    );
  };

  return (
    <div className="varna-radar">
      <svg
        className="varna-radar-svg"
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        role="img"
        aria-label={AXES.map(({ id }) => `${theme(id)}: ${distribution[id] || 0}`).join(', ')}
      >
        <defs>
          <clipPath id={clipId}>
            <path d={dataPath} />
          </clipPath>
        </defs>

        {Array.from({ length: RINGS }, (_, i) => {
          const level = i + 1;
          const radius = R * RING_T[level];
          const pts = axisPoints(AXES.map(() => radius));
          return (
            <path
              key={`ring-${level}`}
              className="varna-radar-ring"
              d={roundedPath(pts, CORNER * RING_T[level])}
            />
          );
        })}

        {AXES.map(({ angle, id }) => {
          const outer = shapePoint(pointAt(angle, axisEndR));
          return (
            <line
              key={`axis-${id}`}
              className="varna-radar-axis"
              x1={CX}
              y1={CY}
              x2={outer.x}
              y2={outer.y}
            />
          );
        })}

        <g className="varna-radar-fill" clipPath={`url(#${clipId})`}>
          <foreignObject x="0" y="0" width={SIZE} height={SIZE}>
            <div
              xmlns="http://www.w3.org/1999/xhtml"
              className="varna-radar-conic"
              style={{ background: conicFill }}
            />
          </foreignObject>
        </g>
      </svg>

      {AXES.map(({ angle, id }) => {
        const { x, y } = shapePoint(pointAt(angle, labelR));
        const count = distribution[id] || 0;
        return (
          <div
            key={`label-${id}`}
            className={`varna-radar-label varna-radar-label--${id}`}
            style={{ left: `${(x / SIZE) * 100}%`, top: `${(y / SIZE) * 100}%` }}
          >
            <TooltipH title={tooltipTitle(id, count)}>
              <span className="varna-radar-label-emoji">{emoji(id)}</span>
            </TooltipH>
          </div>
        );
      })}
    </div>
  );
}
